import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ClipboardCheck, Trash2, CheckCircle2, XCircle, MinusCircle, Send, BarChart3, Timer } from "lucide-react";
import { QuestionEditor, QUESTION_TYPES, newQuestion } from "./quizzes/QuestionEditor";
import QuizTaker from "./quizzes/QuizTaker";
import QuizAnalytics from "./quizzes/QuizAnalytics";
import { pickDefaultSchool } from "../lib/autoSelect";

const EMPTY_QUIZ = { title: "", description: "", due_date: "", time_limit_min: 15, attempts_allowed: 1, questions: [] };

export default function QuizzesPage() {
  const { user } = useAuth();
  const isStudent = user?.role === "student";
  const isTeacher = ["teacher", "academic", "school_admin", "super_admin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_QUIZ);

  // Student "take quiz" state
  const [taking, setTaking] = useState(null);   // full quiz object
  const [result, setResult] = useState(null);   // submission response

  // Teacher dialogs
  const [subsOpen, setSubsOpen] = useState(null);
  const [subs, setSubs] = useState([]);
  const [analyticsOpen, setAnalyticsOpen] = useState(null);

  // ---------- Loaders ----------
  useEffect(() => {
    if (isStudent) {
      api.get("/my/student-record").then(r => {
        if (r.data) { setClassId(r.data.class_id); }
      });
    } else if (isTeacher) {
      api.get("/schools").then(r => {
        const list = r.data || [];
        setSchools(list);
        setSchoolId(prev => prev || pickDefaultSchool(list, user));
      });
    }
  }, [isStudent, isTeacher]);

  useEffect(() => {
    if (!isTeacher || !schoolId) return;
    api.get(`/classes?school_id=${schoolId}`).then(r => {
      const list = r.data || [];
      setClasses(list);
      setClassId(prev => (prev && list.some(c => c.id === prev)) ? prev : (list[0]?.id || ""));
    });
  }, [isTeacher, schoolId]);

  useEffect(() => {
    if (isTeacher && schoolId && classId) {
      setSubjectId(""); // class changed → stale subject may not belong to it
      api.get(`/subjects?school_id=${schoolId}&class_id=${classId}`).then(r => setSubjects(r.data || []));
    }
  }, [isTeacher, schoolId, classId]);

  const load = () => {
    const q = classId ? `?class_id=${classId}` : "";
    api.get(`/quizzes${q}`).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Teacher: create quiz ----------
  const addQ = (type) => setForm(f => ({ ...f, questions: [...f.questions, newQuestion(type)] }));
  const updQ = (idx, patch) => setForm(f => { const arr = [...f.questions]; arr[idx] = { ...arr[idx], ...patch }; return { ...f, questions: arr }; });
  const rmQ = (idx) => setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));

  const saveQuiz = async () => {
    if (!classId || !subjectId) { toast.error("اختر الفصل والمادة"); return; }
    if (!form.title || !form.due_date) { toast.error("العنوان وتاريخ الاستحقاق مطلوبان"); return; }
    if (form.questions.length === 0) { toast.error("أضف سؤالاً واحداً على الأقل"); return; }
    for (const [i, q] of form.questions.entries()) {
      if (q.type === "image_label" && !q.image_url) { toast.error(`السؤال ${i + 1}: ارفع صورة مرقّمة أولاً`); return; }
    }
    try {
      await api.post("/quizzes", {
        school_id: schoolId, class_id: classId, subject_id: subjectId,
        ...form, time_limit_min: Number(form.time_limit_min) || null,
        attempts_allowed: Number(form.attempts_allowed) || 1,
      });
      toast.success("✅ تم إنشاء الواجب التفاعلي");
      setOpenCreate(false); setForm(EMPTY_QUIZ); load();
    } catch (e) { toast.error(e.response?.data?.detail || "خطأ"); }
  };

  const removeQuiz = async (q) => {
    if (!window.confirm(`حذف "${q.title}"؟`)) return;
    try { await api.delete(`/quizzes/${q.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "خطأ"); }
  };

  // ---------- Student: open quiz ----------
  const openQuiz = async (q) => {
    const { data } = await api.get(`/quizzes/${q.id}`);
    setTaking(data); setResult(null);
    try {
      const { data: prev } = await api.get(`/quizzes/${q.id}/my-submission`);
      if (prev) setResult(prev);
    } catch { /* no prior submission */ }
  };

  // ---------- Teacher: view submissions ----------
  const viewSubs = async (q) => {
    setSubsOpen(q);
    const { data } = await api.get(`/quizzes/${q.id}/submissions`);
    setSubs(data || []);
  };

  const classOptions = useMemo(() => classes.map(c => ({ id: c.id, name: c.name })), [classes]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-emerald-600"/>الواجبات التفاعلية</h1>
          <p className="text-sm text-muted-foreground mt-1">7 أنواع أسئلة ذاتية التصحيح — مع مؤقّت وتحليلات ذكية</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isTeacher && (
            <>
              <Select value={schoolId} onValueChange={setSchoolId}>
                <SelectTrigger className="w-52" data-testid="quizzes-school-select"><SelectValue placeholder="اختر المدرسة"/></SelectTrigger>
                <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name_ar || s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger className="w-48" data-testid="quizzes-class-select"><SelectValue placeholder="اختر الفصل"/></SelectTrigger>
                <SelectContent>{classOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          {isTeacher && (
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild><Button disabled={!classId} data-testid="new-quiz-btn"><Plus className="w-4 h-4 me-2"/>واجب تفاعلي جديد</Button></DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>واجب تفاعلي جديد</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>العنوان</Label><Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="quiz-title"/></div>
                    <div><Label>المادة</Label>
                      <Select value={subjectId} onValueChange={setSubjectId}>
                        <SelectTrigger data-testid="quiz-subject"><SelectValue placeholder="اختر"/></SelectTrigger>
                        <SelectContent>{subjects.map(s=><SelectItem key={s.id} value={s.id}>{s.name_ar||s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>تاريخ الاستحقاق</Label><Input type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} data-testid="quiz-due"/></div>
                    <div><Label>المدة الزمنية (دقيقة) — اتركها فارغة بلا مؤقّت</Label><Input type="number" value={form.time_limit_min||""} onChange={e=>setForm({...form,time_limit_min:e.target.value})} data-testid="quiz-time-limit"/></div>
                  </div>
                  <div><Label>الوصف</Label><Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>

                  <div className="flex gap-2 border-t pt-4 flex-wrap">
                    {QUESTION_TYPES.map(qt => (
                      <Button key={qt.type} size="sm" variant="outline" onClick={()=>addQ(qt.type)} data-testid={`add-${qt.type}`}>+ {qt.label}</Button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {form.questions.map((q, i) => (
                      <QuestionEditor key={q.id} q={q} idx={i} onChange={patch=>updQ(i, patch)} onRemove={()=>rmQ(i)} />
                    ))}
                    {form.questions.length === 0 && <div className="text-sm text-center text-muted-foreground p-4 border-dashed border rounded">لا توجد أسئلة بعد. اضغط أحد الأزرار أعلاه لإضافة سؤال.</div>}
                  </div>

                  <Button onClick={saveQuiz} className="w-full" data-testid="quiz-save"><Send className="w-4 h-4 me-2"/>حفظ الواجب</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Quizzes list */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(q => {
          const total = q.total_points || (q.questions||[]).reduce((s,qq)=>s+(qq.points||1),0);
          return (
            <Card key={q.id} className="p-4 border shadow-none card-hover" data-testid={`quiz-${q.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{q.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                    <span>استحقاق: {q.due_date} · {(q.questions||[]).length} سؤال · {total} درجة</span>
                    {q.time_limit_min && <span className="inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 px-1.5 rounded"><Timer className="w-3 h-3"/>{q.time_limit_min} د</span>}
                  </div>
                  {q.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{q.description}</p>}
                </div>
                {isTeacher && <Button variant="ghost" size="icon" onClick={()=>removeQuiz(q)}><Trash2 className="w-4 h-4 text-destructive"/></Button>}
              </div>
              <div className="flex gap-2 mt-3">
                {isStudent && <Button size="sm" onClick={()=>openQuiz(q)} className="flex-1" data-testid={`take-quiz-${q.id}`}>ابدأ الاختبار</Button>}
                {isTeacher && <>
                  <Button size="sm" variant="outline" onClick={()=>viewSubs(q)} className="flex-1" data-testid={`view-subs-${q.id}`}>التسليمات</Button>
                  <Button size="sm" variant="outline" onClick={()=>setAnalyticsOpen(q)} className="flex-1" data-testid={`view-analytics-${q.id}`}>
                    <BarChart3 className="w-4 h-4 me-1"/>التحليلات
                  </Button>
                </>}
              </div>
            </Card>
          );
        })}
        {items.length === 0 && <Card className="col-span-full p-8 text-center text-sm text-muted-foreground border-dashed">لا توجد واجبات تفاعلية بعد</Card>}
      </div>

      {/* Student: take-quiz dialog */}
      <Dialog open={!!taking} onOpenChange={(v)=>!v && setTaking(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {taking && (<>
            <DialogHeader><DialogTitle>{taking.title}</DialogTitle></DialogHeader>
            {result ? (
              <div className="space-y-3">
                <div className={`p-4 rounded-lg text-center ${result.score >= 60 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
                  <div className="text-3xl font-bold">{result.score}%</div>
                  <div className="text-sm mt-1">{result.points_earned} / {result.total_points} درجة</div>
                </div>
                {(result.per_question||[]).map((pq, i) => {
                  const question = (taking.questions||[]).find(x=>x.id===pq.question_id) || {};
                  return (
                    <Card key={pq.question_id} className="p-3 border">
                      <div className="flex justify-between items-start">
                        <div className="text-sm">{i+1}. {question.text || pq.question_id}</div>
                        {pq.correct
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0"/>
                          : pq.partial
                            ? <MinusCircle className="w-5 h-5 text-amber-500 flex-shrink-0"/>
                            : <XCircle className="w-5 h-5 text-red-600 flex-shrink-0"/>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{pq.points}/{pq.max_points} درجة{pq.partial ? " (إجابة جزئية)" : ""}</div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <QuizTaker quiz={taking} onSubmitted={setResult} />
            )}
          </>)}
        </DialogContent>
      </Dialog>

      {/* Teacher: submissions dialog */}
      <Dialog open={!!subsOpen} onOpenChange={(v)=>!v && setSubsOpen(null)}>
        <DialogContent className="max-w-2xl">
          {subsOpen && (<>
            <DialogHeader><DialogTitle>تسليمات: {subsOpen.title}</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {subs.map(s => (
                <Card key={s.id} className="p-3 border flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm">{s.student_name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(s.submitted_at).toLocaleString("ar-SA")} · محاولة #{s.attempt_number}</div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${s.score>=60 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    {s.score}%
                  </div>
                </Card>
              ))}
              {subs.length === 0 && <div className="text-center text-sm text-muted-foreground p-4">لا توجد تسليمات بعد</div>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Teacher: analytics dialog */}
      <Dialog open={!!analyticsOpen} onOpenChange={(v)=>!v && setAnalyticsOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {analyticsOpen && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-600"/>تحليلات: {analyticsOpen.title}</DialogTitle></DialogHeader>
            <QuizAnalytics quiz={analyticsOpen} />
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
